import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Accordion,
    Alert,
    Badge,
    Button,
    Col,
    Container,
    Form,
    Modal,
    Row,
    Spinner,
    Table,
} from 'react-bootstrap';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable/dist/react-select-creatable.esm.js';
import debounce from 'lodash.debounce';
import { v4 as uuidv4 } from 'uuid';
import PageTitle from '../../Components/PageTitle/PageTitle.js';
import CustomButton from '../../Components/Common/CustomButton/CustomButton';
import { useGlobalContext } from '../../Context/index';
import {
    AdsGen,
    AdsGenCreative,
    AdsGenPayload,
    createAdsGen,
    deleteAdsGen,
    getAdsGenList,
    getAdsGenSections,
    updateAdsGen,
} from './api';

type SelectOption = { value: string; label: string };

const TYPE_OPTIONS = [
    { value: 'BannerBar', label: 'BannerBar' },
    { value: 'CubeBar', label: 'CubeBar' },
];

const RESIZE_OPTIONS = [
    { value: 'cover', label: 'cover' },
    { value: 'stretch', label: 'stretch' },
];

const ACTION_TYPE_OPTIONS = [
    { value: 'default', label: 'default' },
    { value: 'externalRedirect', label: 'externalRedirect' },
];

const selectMenuPortalProps = {
    menuPortalTarget: document.body,
    styles: {
        menuPortal: (base: Record<string, unknown>) => ({ ...base, zIndex: 9999 }),
    },
};

const toDatetimeLocal = (iso?: string | null) => {
    if (!iso) return '';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '';
    return new Date(date.getTime() - date.getTimezoneOffset() * 60000)
        .toISOString()
        .slice(0, 16);
};

const fromDatetimeLocal = (value: string) => {
    if (!value) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

const defaultEndDate = () => {
    const date = new Date();
    date.setFullYear(date.getFullYear() + 1);
    return date.toISOString();
};

const formatDate = (dateStr?: string | null) => {
    if (!dateStr) return '—';
    const date = new Date(dateStr);
    return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
};

const emptyCreative = (): AdsGenCreative => ({
    id: uuidv4(),
    displayName: '',
    banner: '',
    resize: 'cover',
    disableBannerClick: true,
    footer: {
        heading: '',
        subHeading: '',
        hideDoctorsText: true,
        config: { hideBlur: true, underlay: true },
    },
    config: { hideBlur: true, underlay: true },
    action: {
        type: 'default',
        default: { copy: '' },
        success: { copy: '' },
        externalRedirectUrl: '',
        registerto_discussionid: '',
    },
    sponsorDetails: { title: '', logo: '' },
});

interface FormState {
    id?: string;
    name: string;
    section: SelectOption | null;
    type: string;
    sponsorTitle: string;
    sponsorLogo: string;
    bannerAspectRatio: string;
    priority: string;
    interval: string;
    isactive: boolean;
    startdate: string;
    enddate: string;
    data: AdsGenCreative[];
}

const emptyForm = (): FormState => ({
    name: '',
    section: null,
    type: 'BannerBar',
    sponsorTitle: '',
    sponsorLogo: '',
    bannerAspectRatio: '1.78',
    priority: '1',
    interval: '1',
    isactive: true,
    startdate: new Date().toISOString(),
    enddate: defaultEndDate(),
    data: [emptyCreative()],
});

const adToForm = (ad: AdsGen): FormState => ({
    id: ad.id,
    name: ad.name || '',
    section: ad.section ? { value: ad.section, label: ad.section } : null,
    type: ad.type || 'BannerBar',
    sponsorTitle: ad.sponsorDetails?.title || '',
    sponsorLogo: ad.sponsorDetails?.logo || '',
    bannerAspectRatio: String(ad.bannerAspectRatio ?? 1.78),
    priority: String(ad.priority ?? 1),
    interval: String(ad.interval ?? 1),
    isactive: Number(ad.isactive) === 1,
    startdate: ad.startdate || new Date().toISOString(),
    enddate: ad.enddate || defaultEndDate(),
    data:
        Array.isArray(ad.data) && ad.data.length
            ? ad.data.map(item => ({
                  ...emptyCreative(),
                  ...item,
                  footer: {
                      ...emptyCreative().footer,
                      ...item.footer,
                      config: {
                          ...emptyCreative().footer?.config,
                          ...item.footer?.config,
                      },
                  },
                  config: {
                      ...emptyCreative().config,
                      ...item.config,
                  },
                  action: {
                      ...emptyCreative().action,
                      ...item.action,
                      default: {
                          ...emptyCreative().action?.default,
                          ...item.action?.default,
                      },
                      success: {
                          ...emptyCreative().action?.success,
                          ...item.action?.success,
                      },
                  },
                  sponsorDetails: {
                      ...emptyCreative().sponsorDetails,
                      ...item.sponsorDetails,
                  },
              }))
            : [emptyCreative()],
});

const formToPayload = (form: FormState): AdsGenPayload => ({
    ...(form.id ? { id: form.id } : {}),
    name: form.name.trim(),
    section: form.section?.value || '',
    type: form.type,
    sponsorDetails: {
        title: form.sponsorTitle.trim(),
        logo: form.sponsorLogo.trim(),
    },
    bannerAspectRatio: Number(form.bannerAspectRatio) || 1.78,
    priority: Number(form.priority) || 1,
    interval: Number(form.interval) || 1,
    isactive: form.isactive ? 1 : 0,
    startdate: form.startdate || null,
    enddate: form.enddate || null,
    data: form.data.map(item => {
        const actionType = item.action?.type || 'default';
        const action: AdsGenCreative['action'] = {
            type: actionType,
            default: {
                copy: item.action?.default?.copy || '',
                ...(item.action?.default?.bgColor
                    ? { bgColor: item.action.default.bgColor }
                    : {}),
                ...(item.action?.default?.textColor
                    ? { textColor: item.action.default.textColor }
                    : {}),
            },
        };

        if (actionType === 'default') {
            action.success = {
                copy: item.action?.success?.copy || '',
                ...(item.action?.success?.bgColor
                    ? { bgColor: item.action.success.bgColor }
                    : {}),
                ...(item.action?.success?.textColor
                    ? { textColor: item.action.success.textColor }
                    : {}),
            };
            if (item.action?.registerto_discussionid?.trim()) {
                action.registerto_discussionid = item.action.registerto_discussionid.trim();
            }
        } else if (actionType === 'externalRedirect') {
            action.externalRedirectUrl = item.action?.externalRedirectUrl || '';
        }

        const creative: AdsGenCreative = {
            id: item.id || uuidv4(),
            displayName: item.displayName || undefined,
            banner: item.banner || '',
            resize: item.resize || 'cover',
            disableBannerClick: Boolean(item.disableBannerClick),
            footer: {
                heading: item.footer?.heading || '',
                subHeading: item.footer?.subHeading || '',
                hideDoctorsText: Boolean(item.footer?.hideDoctorsText),
                config: {
                    hideBlur: Boolean(item.footer?.config?.hideBlur),
                    underlay: Boolean(item.footer?.config?.underlay),
                },
            },
            config: {
                hideBlur: Boolean(item.config?.hideBlur),
                underlay: Boolean(item.config?.underlay),
            },
            action,
        };

        if (item.sponsorDetails?.title || item.sponsorDetails?.logo) {
            creative.sponsorDetails = {
                title: item.sponsorDetails.title || '',
                logo: item.sponsorDetails.logo || '',
            };
        }

        return creative;
    }),
});

const updateCreative = (
    data: AdsGenCreative[],
    index: number,
    updater: (item: AdsGenCreative) => AdsGenCreative,
) => data.map((item, i) => (i === index ? updater(item) : item));

const GenericAds: React.FC = () => {
    const { setShowSuccessToast, setShowErrorToast } = useGlobalContext();

    const [ads, setAds] = useState<AdsGen[]>([]);
    const [sections, setSections] = useState<string[]>([]);
    const [sectionFilter, setSectionFilter] = useState<SelectOption | null>(null);
    const [searchInput, setSearchInput] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [includeInactive, setIncludeInactive] = useState(true);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [error, setError] = useState('');

    const [showFormModal, setShowFormModal] = useState(false);
    const [form, setForm] = useState<FormState>(emptyForm());
    const [formError, setFormError] = useState('');
    const [adToDelete, setAdToDelete] = useState<AdsGen | null>(null);

    const sectionOptions = useMemo(
        () => sections.map(section => ({ value: section, label: section })),
        [sections],
    );

    const fetchSections = useCallback(async () => {
        try {
            const data = await getAdsGenSections();
            setSections(Array.isArray(data) ? data : []);
        } catch {
            // non-blocking
        }
    }, []);

    const fetchAds = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const data = await getAdsGenList({
                section: sectionFilter?.value,
                name: searchQuery,
                isactive: includeInactive ? '' : 1,
                limit: 200,
                offset: 0,
            });
            setAds(data);
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Failed to load ads';
            setError(message);
            setAds([]);
        } finally {
            setLoading(false);
        }
    }, [sectionFilter, searchQuery, includeInactive]);

    useEffect(() => {
        fetchSections();
    }, [fetchSections]);

    useEffect(() => {
        fetchAds();
    }, [fetchAds]);

    const debouncedSetSearch = useMemo(
        () =>
            debounce((value: string) => {
                setSearchQuery(value.trim());
            }, 400),
        [],
    );

    useEffect(() => () => debouncedSetSearch.cancel(), [debouncedSetSearch]);

    const openCreateModal = () => {
        setForm(emptyForm());
        setFormError('');
        setShowFormModal(true);
        fetchSections();
    };

    const openEditModal = (ad: AdsGen) => {
        setForm(adToForm(ad));
        setFormError('');
        setShowFormModal(true);
        fetchSections();
    };

    const handleSave = async () => {
        if (!form.name.trim()) {
            setFormError('Name is required.');
            return;
        }
        if (!form.section?.value) {
            setFormError('Section is required.');
            return;
        }
        if (!form.data.length) {
            setFormError('Add at least one creative.');
            return;
        }
        if (form.data.some(item => !item.banner?.trim())) {
            setFormError('Every creative needs a banner URL.');
            return;
        }

        setActionLoading(true);
        setFormError('');
        try {
            const payload = formToPayload(form);
            if (form.id) {
                await updateAdsGen({ ...payload, id: form.id });
            } else {
                await createAdsGen(payload);
            }
            setShowFormModal(false);
            setShowSuccessToast(true);
            await Promise.all([fetchAds(), fetchSections()]);
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Failed to save ad';
            setFormError(message);
            setShowErrorToast(true);
        } finally {
            setActionLoading(false);
        }
    };

    const handleRestore = async (ad: AdsGen) => {
        setActionLoading(true);
        setError('');
        try {
            await updateAdsGen({
                id: ad.id,
                name: ad.name,
                section: ad.section,
                isactive: 1,
            });
            setShowSuccessToast(true);
            await fetchAds();
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Failed to restore ad';
            setError(message);
            setShowErrorToast(true);
        } finally {
            setActionLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!adToDelete) return;
        setActionLoading(true);
        try {
            await deleteAdsGen(adToDelete.id);
            setAdToDelete(null);
            setShowSuccessToast(true);
            await fetchAds();
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Failed to delete ad';
            setError(message);
            setShowErrorToast(true);
            setAdToDelete(null);
        } finally {
            setActionLoading(false);
        }
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchInput(value);
        if (!value.trim()) {
            debouncedSetSearch.cancel();
            setSearchQuery('');
        } else {
            debouncedSetSearch(value);
        }
    };

    return (
        <Container fluid style={{ padding: '20px' }}>
            <Row className="align-items-center mb-3">
                <Col>
                    <PageTitle>Generic Ads</PageTitle>
                </Col>
                <Col className="text-end">
                    <CustomButton onClick={openCreateModal}>+ Ad</CustomButton>
                </Col>
            </Row>

            <Row className="align-items-center g-2 mb-3">
                <Col md={3}>
                    <Form.Control
                        type="search"
                        placeholder="Search by name..."
                        value={searchInput}
                        onChange={handleSearchChange}
                    />
                </Col>
                <Col md={3}>
                    <Select
                        isClearable
                        placeholder="Filter by section..."
                        options={sectionOptions}
                        value={sectionFilter}
                        onChange={val => setSectionFilter(val as SelectOption | null)}
                        {...selectMenuPortalProps}
                    />
                </Col>
                <Col md="auto">
                    <Form.Check
                        type="switch"
                        id="include-inactive-ads-gen"
                        label="Include inactive"
                        checked={includeInactive}
                        onChange={e => setIncludeInactive(e.target.checked)}
                    />
                </Col>
            </Row>

            {error && (
                <Alert variant="danger" dismissible onClose={() => setError('')}>
                    {error}
                </Alert>
            )}

            {loading ? (
                <div className="text-center py-5">
                    <Spinner animation="border" />
                </div>
            ) : (
                <Table striped bordered hover responsive>
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Section</th>
                            <th>Type</th>
                            <th>Creatives</th>
                            <th>Priority</th>
                            <th>Interval</th>
                            <th>Dates</th>
                            <th>Status</th>
                            <th style={{ width: '180px' }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {ads.length === 0 ? (
                            <tr>
                                <td colSpan={9} className="text-center text-muted">
                                    No ads found
                                </td>
                            </tr>
                        ) : (
                            ads.map(ad => (
                                <tr
                                    key={ad.id}
                                    style={{ opacity: Number(ad.isactive) === 1 ? 1 : 0.6 }}
                                >
                                    <td>
                                        <div style={{ fontWeight: 500 }}>{ad.name}</div>
                                        <div className="text-muted" style={{ fontSize: 12 }}>
                                            {ad.id}
                                        </div>
                                    </td>
                                    <td>{ad.section}</td>
                                    <td>{ad.type}</td>
                                    <td>
                                        <Badge bg="secondary">{ad.data?.length || 0}</Badge>
                                        <div className="d-flex gap-1 mt-1 flex-wrap">
                                            {(ad.data || []).slice(0, 3).map(item =>
                                                item.banner ? (
                                                    <img
                                                        key={item.id || item.banner}
                                                        src={item.banner}
                                                        alt=""
                                                        style={{
                                                            width: 48,
                                                            height: 28,
                                                            objectFit: 'cover',
                                                            borderRadius: 3,
                                                        }}
                                                    />
                                                ) : null,
                                            )}
                                        </div>
                                    </td>
                                    <td>{ad.priority ?? 1}</td>
                                    <td>{ad.interval ?? 1}</td>
                                    <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                                        <div>{formatDate(ad.startdate)}</div>
                                        <div className="text-muted">{formatDate(ad.enddate)}</div>
                                    </td>
                                    <td>
                                        <Badge
                                            bg={Number(ad.isactive) === 1 ? 'success' : 'secondary'}
                                        >
                                            {Number(ad.isactive) === 1 ? 'Active' : 'Inactive'}
                                        </Badge>
                                    </td>
                                    <td>
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            className="me-1 mb-1"
                                            onClick={() => openEditModal(ad)}
                                            disabled={actionLoading}
                                        >
                                            Edit
                                        </Button>
                                        {Number(ad.isactive) === 1 ? (
                                            <Button
                                                variant="outline-danger"
                                                size="sm"
                                                className="mb-1"
                                                onClick={() => setAdToDelete(ad)}
                                                disabled={actionLoading}
                                            >
                                                Delete
                                            </Button>
                                        ) : (
                                            <Button
                                                variant="outline-success"
                                                size="sm"
                                                className="mb-1"
                                                onClick={() => handleRestore(ad)}
                                                disabled={actionLoading}
                                            >
                                                Restore
                                            </Button>
                                        )}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </Table>
            )}

            <Modal
                show={showFormModal}
                onHide={() => !actionLoading && setShowFormModal(false)}
                size="xl"
                backdrop="static"
                scrollable
            >
                <Modal.Header closeButton>
                    <Modal.Title>{form.id ? 'Edit Generic Ad' : 'Add Generic Ad'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {formError && <Alert variant="danger">{formError}</Alert>}
                    <Form>
                        {form.id && (
                            <Form.Group className="mb-3">
                                <Form.Check
                                    type="switch"
                                    id="ads-gen-active"
                                    label="Active"
                                    checked={form.isactive}
                                    onChange={e =>
                                        setForm(prev => ({
                                            ...prev,
                                            isactive: e.target.checked,
                                        }))
                                    }
                                />
                            </Form.Group>
                        )}

                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Name *</Form.Label>
                                    <Form.Control
                                        value={form.name}
                                        onChange={e =>
                                            setForm(prev => ({ ...prev, name: e.target.value }))
                                        }
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Section *</Form.Label>
                                    <CreatableSelect
                                        isClearable
                                        placeholder="Select or create section..."
                                        options={sectionOptions}
                                        value={form.section}
                                        onChange={val =>
                                            setForm(prev => ({
                                                ...prev,
                                                section: val as SelectOption | null,
                                            }))
                                        }
                                        {...selectMenuPortalProps}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Type</Form.Label>
                                    <Form.Select
                                        value={form.type}
                                        onChange={e =>
                                            setForm(prev => ({ ...prev, type: e.target.value }))
                                        }
                                    >
                                        {TYPE_OPTIONS.map(opt => (
                                            <option key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Priority</Form.Label>
                                    <Form.Control
                                        type="number"
                                        value={form.priority}
                                        onChange={e =>
                                            setForm(prev => ({
                                                ...prev,
                                                priority: e.target.value,
                                            }))
                                        }
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Interval</Form.Label>
                                    <Form.Control
                                        type="number"
                                        value={form.interval}
                                        onChange={e =>
                                            setForm(prev => ({
                                                ...prev,
                                                interval: e.target.value,
                                            }))
                                        }
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Banner Aspect Ratio</Form.Label>
                                    <Form.Control
                                        type="number"
                                        step="0.01"
                                        value={form.bannerAspectRatio}
                                        onChange={e =>
                                            setForm(prev => ({
                                                ...prev,
                                                bannerAspectRatio: e.target.value,
                                            }))
                                        }
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Start Date</Form.Label>
                                    <Form.Control
                                        type="datetime-local"
                                        value={toDatetimeLocal(form.startdate)}
                                        onChange={e =>
                                            setForm(prev => ({
                                                ...prev,
                                                startdate:
                                                    fromDatetimeLocal(e.target.value) || '',
                                            }))
                                        }
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>End Date</Form.Label>
                                    <Form.Control
                                        type="datetime-local"
                                        value={toDatetimeLocal(form.enddate)}
                                        onChange={e =>
                                            setForm(prev => ({
                                                ...prev,
                                                enddate:
                                                    fromDatetimeLocal(e.target.value) || '',
                                            }))
                                        }
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Sponsor Title</Form.Label>
                                    <Form.Control
                                        value={form.sponsorTitle}
                                        onChange={e =>
                                            setForm(prev => ({
                                                ...prev,
                                                sponsorTitle: e.target.value,
                                            }))
                                        }
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Sponsor Logo URL</Form.Label>
                                    <Form.Control
                                        value={form.sponsorLogo}
                                        onChange={e =>
                                            setForm(prev => ({
                                                ...prev,
                                                sponsorLogo: e.target.value,
                                            }))
                                        }
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <div className="d-flex align-items-center justify-content-between mb-2">
                            <h5 className="mb-0">Creatives</h5>
                            <Button
                                size="sm"
                                variant="outline-primary"
                                onClick={() =>
                                    setForm(prev => ({
                                        ...prev,
                                        data: [...prev.data, emptyCreative()],
                                    }))
                                }
                            >
                                + Creative
                            </Button>
                        </div>

                        <Accordion alwaysOpen>
                            {form.data.map((creative, index) => (
                                <Accordion.Item
                                    eventKey={String(index)}
                                    key={creative.id || index}
                                >
                                    <Accordion.Header>
                                        Creative {index + 1}
                                        {creative.displayName ? ` · ${creative.displayName}` : ''}
                                        {creative.banner ? ' · has banner' : ''}
                                    </Accordion.Header>
                                    <Accordion.Body>
                                        <div className="d-flex justify-content-end mb-2">
                                            <Button
                                                size="sm"
                                                variant="outline-danger"
                                                disabled={form.data.length === 1}
                                                onClick={() =>
                                                    setForm(prev => ({
                                                        ...prev,
                                                        data: prev.data.filter(
                                                            (_, i) => i !== index,
                                                        ),
                                                    }))
                                                }
                                            >
                                                Remove
                                            </Button>
                                        </div>

                                        <Row>
                                            <Col md={6}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Display Name</Form.Label>
                                                    <Form.Control
                                                        value={creative.displayName || ''}
                                                        onChange={e =>
                                                            setForm(prev => ({
                                                                ...prev,
                                                                data: updateCreative(
                                                                    prev.data,
                                                                    index,
                                                                    item => ({
                                                                        ...item,
                                                                        displayName: e.target.value,
                                                                    }),
                                                                ),
                                                            }))
                                                        }
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={3}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Resize</Form.Label>
                                                    <Form.Select
                                                        value={creative.resize || 'cover'}
                                                        onChange={e =>
                                                            setForm(prev => ({
                                                                ...prev,
                                                                data: updateCreative(
                                                                    prev.data,
                                                                    index,
                                                                    item => ({
                                                                        ...item,
                                                                        resize: e.target.value,
                                                                    }),
                                                                ),
                                                            }))
                                                        }
                                                    >
                                                        {RESIZE_OPTIONS.map(opt => (
                                                            <option
                                                                key={opt.value}
                                                                value={opt.value}
                                                            >
                                                                {opt.label}
                                                            </option>
                                                        ))}
                                                    </Form.Select>
                                                </Form.Group>
                                            </Col>
                                            <Col md={3}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Banner Click</Form.Label>
                                                    <Form.Check
                                                        type="switch"
                                                        id={`disable-banner-click-${index}`}
                                                        label="Disable banner click"
                                                        checked={Boolean(
                                                            creative.disableBannerClick,
                                                        )}
                                                        onChange={e =>
                                                            setForm(prev => ({
                                                                ...prev,
                                                                data: updateCreative(
                                                                    prev.data,
                                                                    index,
                                                                    item => ({
                                                                        ...item,
                                                                        disableBannerClick:
                                                                            e.target.checked,
                                                                    }),
                                                                ),
                                                            }))
                                                        }
                                                    />
                                                </Form.Group>
                                            </Col>
                                        </Row>

                                        <Form.Group className="mb-3">
                                            <Form.Label>Banner URL *</Form.Label>
                                            <Form.Control
                                                value={creative.banner || ''}
                                                onChange={e =>
                                                    setForm(prev => ({
                                                        ...prev,
                                                        data: updateCreative(
                                                            prev.data,
                                                            index,
                                                            item => ({
                                                                ...item,
                                                                banner: e.target.value,
                                                            }),
                                                        ),
                                                    }))
                                                }
                                            />
                                            {creative.banner && (
                                                <img
                                                    src={creative.banner}
                                                    alt=""
                                                    style={{
                                                        marginTop: 8,
                                                        maxWidth: 240,
                                                        maxHeight: 120,
                                                        objectFit: 'contain',
                                                        borderRadius: 4,
                                                    }}
                                                />
                                            )}
                                        </Form.Group>

                                        <Row>
                                            <Col md={6}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Footer Heading</Form.Label>
                                                    <Form.Control
                                                        value={creative.footer?.heading || ''}
                                                        onChange={e =>
                                                            setForm(prev => ({
                                                                ...prev,
                                                                data: updateCreative(
                                                                    prev.data,
                                                                    index,
                                                                    item => ({
                                                                        ...item,
                                                                        footer: {
                                                                            ...item.footer,
                                                                            heading: e.target.value,
                                                                        },
                                                                    }),
                                                                ),
                                                            }))
                                                        }
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Footer Subheading</Form.Label>
                                                    <Form.Control
                                                        value={creative.footer?.subHeading || ''}
                                                        onChange={e =>
                                                            setForm(prev => ({
                                                                ...prev,
                                                                data: updateCreative(
                                                                    prev.data,
                                                                    index,
                                                                    item => ({
                                                                        ...item,
                                                                        footer: {
                                                                            ...item.footer,
                                                                            subHeading:
                                                                                e.target.value,
                                                                        },
                                                                    }),
                                                                ),
                                                            }))
                                                        }
                                                    />
                                                </Form.Group>
                                            </Col>
                                        </Row>

                                        <Row className="mb-3">
                                            <Col md={4}>
                                                <Form.Check
                                                    type="switch"
                                                    id={`hide-doctors-${index}`}
                                                    label="Hide doctors text"
                                                    checked={Boolean(
                                                        creative.footer?.hideDoctorsText,
                                                    )}
                                                    onChange={e =>
                                                        setForm(prev => ({
                                                            ...prev,
                                                            data: updateCreative(
                                                                prev.data,
                                                                index,
                                                                item => ({
                                                                    ...item,
                                                                    footer: {
                                                                        ...item.footer,
                                                                        hideDoctorsText:
                                                                            e.target.checked,
                                                                    },
                                                                }),
                                                            ),
                                                        }))
                                                    }
                                                />
                                            </Col>
                                            <Col md={4}>
                                                <Form.Check
                                                    type="switch"
                                                    id={`hide-blur-${index}`}
                                                    label="Hide blur"
                                                    checked={Boolean(creative.config?.hideBlur)}
                                                    onChange={e =>
                                                        setForm(prev => ({
                                                            ...prev,
                                                            data: updateCreative(
                                                                prev.data,
                                                                index,
                                                                item => ({
                                                                    ...item,
                                                                    config: {
                                                                        ...item.config,
                                                                        hideBlur: e.target.checked,
                                                                    },
                                                                    footer: {
                                                                        ...item.footer,
                                                                        config: {
                                                                            ...item.footer?.config,
                                                                            hideBlur:
                                                                                e.target.checked,
                                                                        },
                                                                    },
                                                                }),
                                                            ),
                                                        }))
                                                    }
                                                />
                                            </Col>
                                            <Col md={4}>
                                                <Form.Check
                                                    type="switch"
                                                    id={`underlay-${index}`}
                                                    label="Underlay"
                                                    checked={Boolean(creative.config?.underlay)}
                                                    onChange={e =>
                                                        setForm(prev => ({
                                                            ...prev,
                                                            data: updateCreative(
                                                                prev.data,
                                                                index,
                                                                item => ({
                                                                    ...item,
                                                                    config: {
                                                                        ...item.config,
                                                                        underlay: e.target.checked,
                                                                    },
                                                                    footer: {
                                                                        ...item.footer,
                                                                        config: {
                                                                            ...item.footer?.config,
                                                                            underlay:
                                                                                e.target.checked,
                                                                        },
                                                                    },
                                                                }),
                                                            ),
                                                        }))
                                                    }
                                                />
                                            </Col>
                                        </Row>

                                        <Row>
                                            <Col md={4}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Action Type</Form.Label>
                                                    <Form.Select
                                                        value={creative.action?.type || 'default'}
                                                        onChange={e =>
                                                            setForm(prev => ({
                                                                ...prev,
                                                                data: updateCreative(
                                                                    prev.data,
                                                                    index,
                                                                    item => ({
                                                                        ...item,
                                                                        action: {
                                                                            ...item.action,
                                                                            type: e.target.value,
                                                                        },
                                                                    }),
                                                                ),
                                                            }))
                                                        }
                                                    >
                                                        {ACTION_TYPE_OPTIONS.map(opt => (
                                                            <option
                                                                key={opt.value}
                                                                value={opt.value}
                                                            >
                                                                {opt.label}
                                                            </option>
                                                        ))}
                                                    </Form.Select>
                                                </Form.Group>
                                            </Col>
                                            <Col md={4}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>CTA Copy</Form.Label>
                                                    <Form.Control
                                                        value={creative.action?.default?.copy || ''}
                                                        onChange={e =>
                                                            setForm(prev => ({
                                                                ...prev,
                                                                data: updateCreative(
                                                                    prev.data,
                                                                    index,
                                                                    item => ({
                                                                        ...item,
                                                                        action: {
                                                                            ...item.action,
                                                                            default: {
                                                                                ...item.action
                                                                                    ?.default,
                                                                                copy: e.target
                                                                                    .value,
                                                                            },
                                                                        },
                                                                    }),
                                                                ),
                                                            }))
                                                        }
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={4}>
                                                {(creative.action?.type || 'default') ===
                                                'default' ? (
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>Success Copy</Form.Label>
                                                        <Form.Control
                                                            value={
                                                                creative.action?.success?.copy || ''
                                                            }
                                                            onChange={e =>
                                                                setForm(prev => ({
                                                                    ...prev,
                                                                    data: updateCreative(
                                                                        prev.data,
                                                                        index,
                                                                        item => ({
                                                                            ...item,
                                                                            action: {
                                                                                ...item.action,
                                                                                success: {
                                                                                    ...item.action
                                                                                        ?.success,
                                                                                    copy: e.target
                                                                                        .value,
                                                                                },
                                                                            },
                                                                        }),
                                                                    ),
                                                                }))
                                                            }
                                                        />
                                                    </Form.Group>
                                                ) : (
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>External Redirect URL</Form.Label>
                                                        <Form.Control
                                                            value={
                                                                creative.action
                                                                    ?.externalRedirectUrl || ''
                                                            }
                                                            onChange={e =>
                                                                setForm(prev => ({
                                                                    ...prev,
                                                                    data: updateCreative(
                                                                        prev.data,
                                                                        index,
                                                                        item => ({
                                                                            ...item,
                                                                            action: {
                                                                                ...item.action,
                                                                                externalRedirectUrl:
                                                                                    e.target.value,
                                                                            },
                                                                        }),
                                                                    ),
                                                                }))
                                                            }
                                                        />
                                                    </Form.Group>
                                                )}
                                            </Col>
                                        </Row>

                                        <Row>
                                            <Col md={3}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>CTA Bg Color</Form.Label>
                                                    <div className="d-flex gap-2 align-items-center">
                                                        <Form.Control
                                                            type="color"
                                                            title="CTA background"
                                                            value={
                                                                creative.action?.default?.bgColor ||
                                                                '#077DE9'
                                                            }
                                                            onChange={e =>
                                                                setForm(prev => ({
                                                                    ...prev,
                                                                    data: updateCreative(
                                                                        prev.data,
                                                                        index,
                                                                        item => ({
                                                                            ...item,
                                                                            action: {
                                                                                ...item.action,
                                                                                default: {
                                                                                    ...item.action
                                                                                        ?.default,
                                                                                    bgColor:
                                                                                        e.target
                                                                                            .value,
                                                                                },
                                                                            },
                                                                        }),
                                                                    ),
                                                                }))
                                                            }
                                                            style={{
                                                                width: 48,
                                                                padding: 2,
                                                            }}
                                                        />
                                                        <Form.Control
                                                            placeholder="#077DE9"
                                                            value={
                                                                creative.action?.default?.bgColor ||
                                                                ''
                                                            }
                                                            onChange={e =>
                                                                setForm(prev => ({
                                                                    ...prev,
                                                                    data: updateCreative(
                                                                        prev.data,
                                                                        index,
                                                                        item => ({
                                                                            ...item,
                                                                            action: {
                                                                                ...item.action,
                                                                                default: {
                                                                                    ...item.action
                                                                                        ?.default,
                                                                                    bgColor:
                                                                                        e.target
                                                                                            .value,
                                                                                },
                                                                            },
                                                                        }),
                                                                    ),
                                                                }))
                                                            }
                                                        />
                                                    </div>
                                                </Form.Group>
                                            </Col>
                                            <Col md={3}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>CTA Text Color</Form.Label>
                                                    <div className="d-flex gap-2 align-items-center">
                                                        <Form.Control
                                                            type="color"
                                                            title="CTA text"
                                                            value={
                                                                creative.action?.default
                                                                    ?.textColor || '#ffffff'
                                                            }
                                                            onChange={e =>
                                                                setForm(prev => ({
                                                                    ...prev,
                                                                    data: updateCreative(
                                                                        prev.data,
                                                                        index,
                                                                        item => ({
                                                                            ...item,
                                                                            action: {
                                                                                ...item.action,
                                                                                default: {
                                                                                    ...item.action
                                                                                        ?.default,
                                                                                    textColor:
                                                                                        e.target
                                                                                            .value,
                                                                                },
                                                                            },
                                                                        }),
                                                                    ),
                                                                }))
                                                            }
                                                            style={{
                                                                width: 48,
                                                                padding: 2,
                                                            }}
                                                        />
                                                        <Form.Control
                                                            placeholder="#ffffff"
                                                            value={
                                                                creative.action?.default
                                                                    ?.textColor || ''
                                                            }
                                                            onChange={e =>
                                                                setForm(prev => ({
                                                                    ...prev,
                                                                    data: updateCreative(
                                                                        prev.data,
                                                                        index,
                                                                        item => ({
                                                                            ...item,
                                                                            action: {
                                                                                ...item.action,
                                                                                default: {
                                                                                    ...item.action
                                                                                        ?.default,
                                                                                    textColor:
                                                                                        e.target
                                                                                            .value,
                                                                                },
                                                                            },
                                                                        }),
                                                                    ),
                                                                }))
                                                            }
                                                        />
                                                    </div>
                                                </Form.Group>
                                            </Col>
                                            {(creative.action?.type || 'default') === 'default' && (
                                                <>
                                                    <Col md={3}>
                                                        <Form.Group className="mb-3">
                                                            <Form.Label>
                                                                Success Bg Color
                                                            </Form.Label>
                                                            <div className="d-flex gap-2 align-items-center">
                                                                <Form.Control
                                                                    type="color"
                                                                    title="Success background"
                                                                    value={
                                                                        creative.action?.success
                                                                            ?.bgColor || '#1f7ec6'
                                                                    }
                                                                    onChange={e =>
                                                                        setForm(prev => ({
                                                                            ...prev,
                                                                            data: updateCreative(
                                                                                prev.data,
                                                                                index,
                                                                                item => ({
                                                                                    ...item,
                                                                                    action: {
                                                                                        ...item.action,
                                                                                        success: {
                                                                                            ...item
                                                                                                .action
                                                                                                ?.success,
                                                                                            bgColor:
                                                                                                e
                                                                                                    .target
                                                                                                    .value,
                                                                                        },
                                                                                    },
                                                                                }),
                                                                            ),
                                                                        }))
                                                                    }
                                                                    style={{
                                                                        width: 48,
                                                                        padding: 2,
                                                                    }}
                                                                />
                                                                <Form.Control
                                                                    placeholder="#1f7ec6"
                                                                    value={
                                                                        creative.action?.success
                                                                            ?.bgColor || ''
                                                                    }
                                                                    onChange={e =>
                                                                        setForm(prev => ({
                                                                            ...prev,
                                                                            data: updateCreative(
                                                                                prev.data,
                                                                                index,
                                                                                item => ({
                                                                                    ...item,
                                                                                    action: {
                                                                                        ...item.action,
                                                                                        success: {
                                                                                            ...item
                                                                                                .action
                                                                                                ?.success,
                                                                                            bgColor:
                                                                                                e
                                                                                                    .target
                                                                                                    .value,
                                                                                        },
                                                                                    },
                                                                                }),
                                                                            ),
                                                                        }))
                                                                    }
                                                                />
                                                            </div>
                                                        </Form.Group>
                                                    </Col>
                                                    <Col md={3}>
                                                        <Form.Group className="mb-3">
                                                            <Form.Label>
                                                                Success Text Color
                                                            </Form.Label>
                                                            <div className="d-flex gap-2 align-items-center">
                                                                <Form.Control
                                                                    type="color"
                                                                    title="Success text"
                                                                    value={
                                                                        creative.action?.success
                                                                            ?.textColor ||
                                                                        '#ffffff'
                                                                    }
                                                                    onChange={e =>
                                                                        setForm(prev => ({
                                                                            ...prev,
                                                                            data: updateCreative(
                                                                                prev.data,
                                                                                index,
                                                                                item => ({
                                                                                    ...item,
                                                                                    action: {
                                                                                        ...item.action,
                                                                                        success: {
                                                                                            ...item
                                                                                                .action
                                                                                                ?.success,
                                                                                            textColor:
                                                                                                e
                                                                                                    .target
                                                                                                    .value,
                                                                                        },
                                                                                    },
                                                                                }),
                                                                            ),
                                                                        }))
                                                                    }
                                                                    style={{
                                                                        width: 48,
                                                                        padding: 2,
                                                                    }}
                                                                />
                                                                <Form.Control
                                                                    placeholder="#ffffff"
                                                                    value={
                                                                        creative.action?.success
                                                                            ?.textColor || ''
                                                                    }
                                                                    onChange={e =>
                                                                        setForm(prev => ({
                                                                            ...prev,
                                                                            data: updateCreative(
                                                                                prev.data,
                                                                                index,
                                                                                item => ({
                                                                                    ...item,
                                                                                    action: {
                                                                                        ...item.action,
                                                                                        success: {
                                                                                            ...item
                                                                                                .action
                                                                                                ?.success,
                                                                                            textColor:
                                                                                                e
                                                                                                    .target
                                                                                                    .value,
                                                                                        },
                                                                                    },
                                                                                }),
                                                                            ),
                                                                        }))
                                                                    }
                                                                />
                                                            </div>
                                                        </Form.Group>
                                                    </Col>
                                                </>
                                            )}
                                        </Row>

                                        {(creative.action?.type || 'default') === 'default' && (
                                            <Form.Group className="mb-3">
                                                <Form.Label>
                                                    Register to Discussion ID (optional)
                                                </Form.Label>
                                                <Form.Control
                                                    value={
                                                        creative.action?.registerto_discussionid ||
                                                        ''
                                                    }
                                                    onChange={e =>
                                                        setForm(prev => ({
                                                            ...prev,
                                                            data: updateCreative(
                                                                prev.data,
                                                                index,
                                                                item => ({
                                                                    ...item,
                                                                    action: {
                                                                        ...item.action,
                                                                        registerto_discussionid:
                                                                            e.target.value,
                                                                    },
                                                                }),
                                                            ),
                                                        }))
                                                    }
                                                />
                                            </Form.Group>
                                        )}
                                    </Accordion.Body>
                                </Accordion.Item>
                            ))}
                        </Accordion>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => setShowFormModal(false)}
                        disabled={actionLoading}
                    >
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleSave} disabled={actionLoading}>
                        {actionLoading ? <Spinner size="sm" animation="border" /> : 'Save'}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal
                show={!!adToDelete}
                onHide={() => !actionLoading && setAdToDelete(null)}
            >
                <Modal.Header closeButton>
                    <Modal.Title>Delete Generic Ad</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Soft-delete <strong>{adToDelete?.name}</strong>? It will be marked inactive.
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => setAdToDelete(null)}
                        disabled={actionLoading}
                    >
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handleDelete} disabled={actionLoading}>
                        {actionLoading ? <Spinner size="sm" animation="border" /> : 'Delete'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default GenericAds;
